import { AreaChart } from '@mantine/charts';
import {
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MetricHint, type MetricHintKey } from 'src/secret-menu/billing/MetricHint';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { useBillingSnapshots } from 'src/secret-menu/billing/hooks/useBillingSnapshots';
import { applyLens, engagementOf, indexSeries, trendOf, type Lens } from './lenses';
import { billingFor, formatPercent, percentOfStated, type RevenueModel } from './revenue';
import { monthKeyOfDate, USAGE_PARTS, type Firm, type UsageModel } from './usageModel';
import FirmUsersCard from './FirmUsersCard';
import {
  describePricing,
  PRICING_CATEGORY_COLORS,
  PRICING_CATEGORY_LABELS,
} from '../revenue/pricing';
import { ChangeArrow, formatRevenue } from '../revenue/ui';

type Props = {
  model: UsageModel;
  firm: Firm;
  month: string;
  lens: Lens;
  today: Date;
  revenue: RevenueModel | null;
  onEditBilling: (detail: BillingDetail) => void;
  onBack: () => void;
};

const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

/** One figure in the firm's revenue card; whatever sits beneath it lines up along the bottom. */
function Tile({
  label,
  hint,
  value,
  change,
  children,
}: {
  label: string;
  hint?: MetricHintKey;
  value: string;
  change?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <Stack
      gap={6}
      p="md"
      h="100%"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        background: 'var(--mantine-color-default-hover)',
      }}
    >
      <Group gap={4} wrap="nowrap">
        <Text size="textXs" c="dimmed">
          {label}
        </Text>
        {hint && <MetricHint hint={hint} />}
      </Group>
      <Group gap="xs" align="baseline" wrap="nowrap">
        <Text size="xl" fw={FONT_WEIGHTS.semibold} style={tabular}>
          {value}
        </Text>
        {change}
      </Group>
      {children && (
        <Stack gap={4} mt="auto">
          {children}
        </Stack>
      )}
    </Stack>
  );
}

function FamilyTile({
  label,
  hint,
  count,
  stated,
  color,
  change,
}: {
  label: string;
  hint: MetricHintKey;
  count: number;
  stated: number | null;
  color: string;
  change?: React.ReactNode;
}) {
  const percent = percentOfStated(count, stated);
  return (
    <Tile label={label} hint={hint} value={count.toLocaleString('en-GB')} change={change}>
      {percent === null ? (
        <Text size="textXs" c="dimmed">
          No stated families to compare with
        </Text>
      ) : (
        <>
          <Progress value={Math.min(percent, 100)} color={color} size="sm" />
          <Text size="textXs" c="dimmed">
            {formatPercent(percent)} of {stated?.toLocaleString('en-GB')} stated
          </Text>
        </>
      )}
    </Tile>
  );
}

const monthYear = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

function RevenueCard({
  detail,
  firm,
  today,
  onEdit,
}: {
  detail: BillingDetail | undefined;
  firm: Firm;
  today: Date;
  onEdit: (detail: BillingDetail) => void;
}) {
  const { data: snapshots } = useBillingSnapshots();
  if (!detail) {
    return (
      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb={4}>
          Revenue
        </Text>
        <Text size="textXs" c="dimmed">
          No billing record for this firm.
        </Text>
      </Paper>
    );
  }

  const category = detail.pricingCategory;
  const trial = category === 'TRIAL';
  const stated = detail.statedBillableUnits;
  const fullPence = detail.fullAdoptionMonthlyBillPence;
  const billPence = detail.monthlyBillPence ?? 0;
  // Last month-end: today's clients less the ones added this month, and the firm's own snapshot.
  const lastMonthKey = monthKeyOfDate(
    new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1)),
  );
  const clientsLastMonth =
    detail.billableClientsCount - (firm.series.get(monthKeyOfDate(today))?.newClients ?? 0);
  const unitsLastMonth = snapshots?.find(
    snapshot => snapshot.companyId === detail.companyId && snapshot.month.startsWith(lastMonthKey),
  )?.activeBillableUnitCount;

  let pricing = describePricing(detail);
  if (trial) {
    pricing += detail.billingStartDate
      ? ` · starts ${monthYear(detail.billingStartDate)}`
      : ' · no start date';
  }

  return (
    <Paper p="lg" withBorder radius="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={4}>
          <Group gap="xs">
            <Text size="textSm" c="dimmed">
              {trial ? 'Monthly bill once billing starts' : 'Monthly bill'}
            </Text>
            <Badge variant="light" color={PRICING_CATEGORY_COLORS[category]}>
              {PRICING_CATEGORY_LABELS[category]}
            </Badge>
          </Group>
          <Text fz={44} lh={1.1} fw={FONT_WEIGHTS.semibold} style={tabular}>
            {formatRevenue(billPence)}
          </Text>
          <Text size="textSm" c="dimmed">
            {pricing}
          </Text>
        </Stack>
        <Button size="xs" variant="light" onClick={() => onEdit(detail)}>
          Edit billing
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm" mt="lg">
        <Tile
          label="Families stated by the firm"
          hint="statedFamilies"
          value={stated === null ? '—' : stated.toLocaleString('en-GB')}
        >
          <Text size="textXs" c="dimmed">
            {stated === null ? 'Add in Edit billing' : 'Proxy for billable units at full adoption'}
          </Text>
        </Tile>
        <Tile
          label="Clients on Afternoon"
          hint="allClients"
          value={detail.billableClientsCount.toLocaleString('en-GB')}
          change={
            <ChangeArrow
              current={detail.billableClientsCount}
              previous={clientsLastMonth}
              period="MoM"
            />
          }
        >
          <Text size="textXs" c="dimmed">
            {detail.activeClientsCount.toLocaleString('en-GB')} active (2+ actions)
          </Text>
        </Tile>
        <FamilyTile
          label="Billable units"
          hint="billedUnits"
          count={detail.activeBillableUnitCount}
          stated={stated}
          color="green"
          change={
            unitsLastMonth === undefined ? undefined : (
              <ChangeArrow
                current={detail.activeBillableUnitCount}
                previous={unitsLastMonth}
                period="MoM"
              />
            )
          }
        />
        <Tile
          label="At full adoption"
          hint="fullAdoption"
          value={fullPence === null ? '—' : formatRevenue(fullPence)}
        >
          <Text size="textXs" c="dimmed">
            {fullPence === null
              ? 'Add stated families to see it'
              : `${formatRevenue(Math.max(0, fullPence - billPence))} a month more than now${
                  billPence > 0
                    ? ` (${Math.round(((fullPence - billPence) / billPence) * 100)}% increase)`
                    : ''
                }`}
          </Text>
        </Tile>
      </SimpleGrid>
    </Paper>
  );
}

/**
 * What a firm used in the month, busiest first: each kind of action with its count, a bar against
 * the busiest, and its share of the total. Kinds with nothing that month sit greyed out at the end.
 */
function UsageBreakdown({ parts }: { parts: { metric: string; value: number }[] }) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  const busiest = Math.max(...parts.map(part => part.value), 0);
  const ranked = [...parts].sort((a, b) => b.value - a.value);
  if (total === 0) {
    return (
      <Text size="textSm" c="dimmed">
        No activity this month.
      </Text>
    );
  }
  return (
    <Stack gap="sm">
      {ranked.map(part => (
        <Stack key={part.metric} gap={4} opacity={part.value === 0 ? 0.45 : 1}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="textSm">{part.metric}</Text>
            <Text size="textSm" style={tabular}>
              <Text span fw={FONT_WEIGHTS.semibold} inherit>
                {part.value.toLocaleString('en-GB')}
              </Text>
              <Text span c="dimmed" inherit>
                {` · ${Math.round((part.value / total) * 100)}%`}
              </Text>
            </Text>
          </Group>
          <Progress
            value={busiest === 0 ? 0 : (part.value / busiest) * 100}
            size="sm"
            color="orange.6"
          />
        </Stack>
      ))}
    </Stack>
  );
}

export default function FirmDetail({
  model,
  firm,
  month,
  lens,
  today,
  revenue,
  onEditBilling,
  onBack,
}: Props) {
  const chartLens: Lens = lens === 'share' ? 'raw' : lens;
  const months = model.months.filter(key => key >= firm.first);

  const rawSeries = months.map(key =>
    applyLens(firm.series.get(key)?.usage ?? 0, key, chartLens === 'indexed' ? 'raw' : chartLens, {
      model,
      today,
      firm,
    }),
  );
  const series = chartLens === 'indexed' ? indexSeries(rawSeries) : rawSeries;

  const usageData = months.map((key, index) => ({
    month: model.monthLabels.get(key) ?? key,
    usage: series[index],
  }));

  const counts = firm.series.get(month);
  const breakdownData = USAGE_PARTS.map(item => ({
    metric: item.label,
    value: counts?.[item.key] ?? 0,
  }));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Anchor size="textXs" onClick={onBack}>
            ← Back to all firms
          </Anchor>
          <Title order={3}>{firm.name}</Title>
          <Text size="textXs" c="dimmed">
            First active {model.monthLabels.get(firm.first)} · last active{' '}
            {model.monthLabels.get(firm.last)} · {firm.activeMonths} active months
          </Text>
        </Stack>
        <Group gap="xs">
          <Badge variant="light" color="gray">
            {engagementOf(counts?.usage ?? 0, counts?.newClients ?? 0)}
          </Badge>
          <Badge variant="light">{trendOf(firm, month, model)}</Badge>
        </Group>
      </Group>

      <RevenueCard
        detail={billingFor(revenue, firm.name)}
        firm={firm}
        today={today}
        onEdit={onEditBilling}
      />

      <FirmUsersCard companyId={billingFor(revenue, firm.name)?.companyId} />

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Paper p="md" withBorder radius="md">
          <Text fw={FONT_WEIGHTS.semibold} mb="sm">
            Usage over time
          </Text>
          <AreaChart
            h={220}
            data={usageData}
            dataKey="month"
            series={[
              { name: 'usage', label: 'ABC excluding adding new clients', color: 'brand.6' },
            ]}
            curveType="linear"
            withDots={false}
            connectNulls={false}
          />
        </Paper>

        <Paper p="md" withBorder radius="md">
          <Group justify="space-between" align="baseline" mb="sm">
            <Text fw={FONT_WEIGHTS.semibold}>What they use</Text>
            <Text size="textXs" c="dimmed">
              {model.monthLabels.get(month)}
            </Text>
          </Group>
          <UsageBreakdown parts={breakdownData} />
        </Paper>
      </SimpleGrid>

      <Paper p="md" withBorder radius="md">
        <Text fw={FONT_WEIGHTS.semibold} mb="sm">
          Month by month
        </Text>
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Month</Table.Th>
                <Table.Th ta="right">ABC excluding adding new clients</Table.Th>
                <Table.Th ta="right">New clients added</Table.Th>
                {USAGE_PARTS.map(item => (
                  <Table.Th key={item.key} ta="right">
                    {item.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...months].reverse().map(key => {
                const monthCounts = firm.series.get(key);
                return (
                  <Table.Tr key={key}>
                    <Table.Td>{model.monthLabels.get(key)}</Table.Td>
                    <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                      {(monthCounts?.usage ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    <Table.Td ta="right">
                      {(monthCounts?.newClients ?? 0).toLocaleString('en-GB')}
                    </Table.Td>
                    {USAGE_PARTS.map(item => (
                      <Table.Td key={item.key} ta="right">
                        {(monthCounts?.[item.key] ?? 0).toLocaleString('en-GB')}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
